/**
 * Estados de facturación para Company
 * Debe coincidir exactamente con el enum BillingStatus del backend
 */
export enum BillingStatus {
  /**
   * Pendiente de configurar Stripe
   */
  INCOMPLETE = 'INCOMPLETE',

  /**
   * Activo y al corriente de pagos
   */
  ACTIVE = 'ACTIVE',

  /**
   * Suscripción en período de prueba
   */
  TRIAL_ACTIVE = 'TRIAL_ACTIVE',

  /**
   * Pago atrasado
   */
  PAST_DUE = 'PAST_DUE',

  /**
   * Suspendido por impago
   */
  SUSPENDED = 'SUSPENDED',

  /**
   * Período de prueba expirado
   */
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',

  /**
   * Cancelado
   */
  CANCELLED = 'CANCELLED'
}

/**
 * Estados de facturación para PromotionRedemption
 * Debe coincidir exactamente con el enum RedemptionBillingStatus del backend
 */
export enum RedemptionBillingStatus {
  /**
   * Pendiente de facturar
   */
  PENDING = 'PENDING',

  /**
   * Facturado correctamente
   */
  BILLED = 'BILLED',

  /**
   * Error en la facturación
   */
  FAILED = 'FAILED',

  /**
   * Reembolsado
   */
  REFUNDED = 'REFUNDED',

  /**
   * Excluido de facturación (test, fraude, etc.)
   */
  EXCLUDED = 'EXCLUDED'
}

/**
 * Obtiene el color asociado a un estado de facturación de Company
 */
export function getBillingStatusColor(status: BillingStatus | string): string {
  switch (status) {
    case BillingStatus.ACTIVE:
      return 'primary';
    case BillingStatus.TRIAL_ACTIVE:
      return 'accent';
    case BillingStatus.INCOMPLETE:
      return 'accent';
    case BillingStatus.PAST_DUE:
      return 'warn';
    case BillingStatus.SUSPENDED:
      return 'warn';
    case BillingStatus.TRIAL_EXPIRED:
      return 'warn';
    case BillingStatus.CANCELLED:
      return 'basic';
    default:
      return 'basic';
  }
}

/**
 * Obtiene el color asociado a un estado de facturación de Redemption
 */
export function getRedemptionBillingStatusColor(status: RedemptionBillingStatus | string): string {
  switch (status) {
    case RedemptionBillingStatus.BILLED:
      return 'primary';
    case RedemptionBillingStatus.PENDING:
      return 'accent';
    case RedemptionBillingStatus.FAILED:
      return 'warn';
    case RedemptionBillingStatus.REFUNDED:
      return 'accent';
    case RedemptionBillingStatus.EXCLUDED:
      return 'basic';
    default:
      return 'basic';
  }
}

/**
 * Obtiene un label legible para un estado de facturación de Company
 */
export function getBillingStatusLabel(status: BillingStatus | string): string {
  switch (status) {
    case BillingStatus.INCOMPLETE:
      return 'Incompleto';
    case BillingStatus.ACTIVE:
      return 'Activo';
    case BillingStatus.TRIAL_ACTIVE:
      return 'Período de prueba';
    case BillingStatus.PAST_DUE:
      return 'Pago Atrasado';
    case BillingStatus.SUSPENDED:
      return 'Suspendido';
    case BillingStatus.TRIAL_EXPIRED:
      return 'Prueba expirada';
    case BillingStatus.CANCELLED:
      return 'Cancelado';
    default:
      return status || 'Desconocido';
  }
}

/**
 * Obtiene un label legible para un estado de facturación de Redemption
 */
export function getRedemptionBillingStatusLabel(status: RedemptionBillingStatus | string): string {
  switch (status) {
    case RedemptionBillingStatus.PENDING:
      return 'Pendiente';
    case RedemptionBillingStatus.BILLED:
      return 'Facturado';
    case RedemptionBillingStatus.FAILED:
      return 'Error';
    case RedemptionBillingStatus.REFUNDED:
      return 'Reembolsado';
    case RedemptionBillingStatus.EXCLUDED:
      return 'Excluido';
    default:
      return status || 'Desconocido';
  }
}

/**
 * Obtiene todos los valores del enum BillingStatus
 */
export function getAllBillingStatuses(): BillingStatus[] {
  return Object.values(BillingStatus);
}

/**
 * Obtiene todos los valores del enum RedemptionBillingStatus
 */
export function getAllRedemptionBillingStatuses(): RedemptionBillingStatus[] {
  return Object.values(RedemptionBillingStatus);
}
